import React, { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { sidebarBreakPoint } from '../Settings/config';
import { Sidebar } from 'primereact/sidebar';
import { Button, ButtonProps } from 'primereact/button';
import { IconType } from 'primereact/utils';
import { Loading } from './system/Loading';
import { IntlShape, useIntl } from 'react-intl';
import { ProgressSpinner } from 'primereact/progressspinner';

export interface LayoutProps {
  title?: ReactNode | string | undefined;
  sidebarTitle?: ReactNode | string | undefined;
  mainTitle?: ReactNode | string | undefined;
  sidebarContent?: ReactNode | undefined;
  sidebarWidth?: number | undefined;
  headerImage?: string;
  sidebarButtonIcon?: IconType<ButtonProps>;
  showSidebarInitially?: boolean;
  sidebarVisible?: boolean;
  setSidebarVisible?: (visible: boolean) => void;
  loading?: boolean;
  showSidebar?: boolean;
  isAdminAreaSystem?: boolean;
  isAdminJobEntriesManagement?: boolean;
  isEditWorkEnrichment?: boolean;
  loadingOverlay?: boolean;
  sidebarBreakpoint?: 'md' | 'lg' | 'xl';
}

export const Layout: React.FC<PropsWithChildren<LayoutProps>> = (props: PropsWithChildren<LayoutProps>): ReactNode => {
  const intl: IntlShape = useIntl();
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [sidebarVisible, _setSidebarVisible] = useState<boolean>(!!props.showSidebarInitially);

  const isAdminAreaSystem = props.isAdminAreaSystem ?? false;

  const sidebarBreakpoint = props.sidebarBreakpoint ?? 'xl';

  const mobileSidebarClass =
    sidebarBreakpoint === 'lg'
      ? 'admin-sidebar-mobile'
      : 'xl:hidden';

  const fixedSidebarClass =
    sidebarBreakpoint === 'lg'
      ? 'admin-sidebar-fixed'
      : 'hidden xl:block';

  const sidebarWClass: string = props.sidebarWidth && props.sidebarWidth < 30 && props.sidebarWidth > 0 ? `sm:max-w-${props.sidebarWidth}rem` : 'sm:max-w-30rem';

  const setSidebarVisible = (visible: boolean): void => {
    if (props.sidebarVisible)
      return props.setSidebarVisible(visible);
    return _setSidebarVisible(visible);
  };

  useEffect((): void => {
    if (props.sidebarVisible === undefined) return;
    _setSidebarVisible(props.sidebarVisible);
  }, [props.sidebarVisible]);

  useEffect((): () => void => {
    window.addEventListener('resize', (): void => {
      setWidth(window.innerWidth);
    });
    return (): void => {
      window.removeEventListener('resize', (): void => {
        setWidth(window.innerWidth);
        if (window.innerWidth > sidebarBreakPoint && sidebarVisible) {
          setSidebarVisible(false);
        }
      });
    };
  }, []);

  const renderSidebarContent = (): ReactNode => <div className={'p-3 flex flex-column gap-2'}>
    {props.sidebarTitle &&
      <h2 style={{fontSize: 'calc(15px + 1.8vw)'}} className={'md:text-3xl'}>{props.sidebarTitle}</h2>}
    {props.sidebarContent}
  </div>;

  return (
    <div className={'relative'}>
      {props.sidebarContent &&
        <Sidebar
          key={'sidebar-tgl'} className={`${mobileSidebarClass} w-full max-w-screen ${sidebarWClass}`}
          visible={sidebarVisible}
          dismissable={false}
          modal={false}
          onHide={(): void => setSidebarVisible(false)}
        >
          {!props.loading && renderSidebarContent()}
        </Sidebar>
      }
      {props.sidebarContent &&
        <Button
          className={`${mobileSidebarClass} fixed my-2 opacity-100 z-5`}
          icon={props.sidebarButtonIcon || 'pi pi-angle-right'}
          pt={{root: {className: 'border-noround-left'}}}
          size={'large'}
          raised
          onClick={(e): void => {
            e.preventDefault();
            setSidebarVisible(true);
          }}
        />
      }
      <div key={'layout-grid'} className={'flex flex-column'}>
        {props.title &&
          <div
            className={'col-12 p-3 surface-100 bg-cover bg-center flex justify-content-center'}
            style={props.headerImage ? {backgroundImage: `url(${props.headerImage})`} : {}}
          >
            <div className={'mx-auto bg-white-alpha-80 px-3 border-round-3xl shadowed'}>
              <h1 style={{textAlign: 'center', fontSize: 'calc(15px + 1.8vw)'}} className={'md:text-3xl m-1 p-1'}>
                {props.title}
              </h1>
            </div>
          </div>
        }
        <div className={'flex flex-row'}>
          {props.sidebarContent &&
            <div
              key={'sidebar-fix'}
              className={
                `${fixedSidebarClass} border-right-1 surface-border ${
                  isAdminAreaSystem ? 'flex-none' : `w-full ${sidebarWClass}`
                }`
              }
              style={isAdminAreaSystem ? {width: '17rem'} : undefined}
            >
              {(!props.loading && renderSidebarContent()) || (props.loadingOverlay && renderSidebarContent())}
            </div>
          }
          {props.loading && !props.loadingOverlay && (
            <main key={'main'} className={'w-full h-full p-3'}>
              <Loading/>
            </main>
          )}

          {(!props.loading || props.loadingOverlay) && (
            <main
              key={'main'}
              className={'flex flex-column gap-4 py-3 mx-auto w-full'}
              style={
                isAdminAreaSystem
                  ? {
                      maxWidth: '100%',
                      minWidth: '1500px',
                      paddingLeft: '2em',
                      paddingRight: '2em'
                    }
                  : {
                      maxWidth: '1500px'
                    }
              }
            >
              {props.mainTitle && (
                <div className={'px-3'}>
                  <h2
                    style={{fontSize: 'calc(15px + 1.8vw)'}}
                    className={'md:text-3xl'}
                  >
                    {props.mainTitle}
                  </h2>
                </div>
              )}

              {props.children}
            </main>
          )}

          {props.loading && props.loadingOverlay && (
            <div
              className='fixed top-0 left-0 w-screen h-screen flex align-items-center justify-content-center'
              style={{
                zIndex: 9999,
                backgroundColor: 'rgba(255, 255, 255, 0.75)'
              }}
            >
              <div style={{ display: 'inline-grid', gap: '1em' }}>
                <ProgressSpinner style={{ width: '50px', height: '50px', stroke: '#111827' }} />
                {props.isAdminJobEntriesManagement &&
                  <div className='text-xl text-primary-900 font-bold' style={{ justifyItems: 'center' }}>
                    <div>{intl.formatMessage({id: 'importJobEntriesManagement.reimport.loading.firstLine'})}</div>
                    <div>{intl.formatMessage({id: 'importJobEntriesManagement.reimport.loading.secondLine'})}</div>
                  </div>
                }
                {props.isEditWorkEnrichment &&
                  <div className='text-xl text-primary-900 font-bold' style={{ justifyItems: 'center' }}>
                    <div>{intl.formatMessage({id: 'work.enrichment.loading.firstLine'})}</div>
                    <div>{intl.formatMessage({id: 'work.enrichment.loading.secondLine'})}</div>
                  </div>
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);
};
